/**
 * The Market Intelligence data source.
 *
 * Every method is shaped like the REST endpoint it will become: the parameter
 * object is that endpoint's query string, and the return type is its response
 * body. Today it is served by a seeded generator; when `/api/market/*` ships,
 * write an `HttpMarketIntelligenceSource` whose methods are one `api.get` each
 * and swap the instance below. Pages, hooks, and every pure helper in this
 * folder are untouched by that change.
 *
 * Series responses carry ALL blocks and both streams rather than the one the
 * page is showing. That is deliberate on two counts: the peak toggle must not
 * refetch, and the y-axis must span every series so hiding one cannot rescale
 * the chart. Both need the union client-side anyway.
 */

import { httpMarketIntelligenceSource } from "./http-source";

import type {
  CapacityPoint,
  MarketCapacity,
  ForwardCurveVintage,
  HourlyIndexRow,
  IndexDailyRow,
  IndexRow,
  IsoCode,
  MarketCommodity,
  MarketTopology,
  MarketWindow,
  RecCertificate,
  StripPeriod,
  StripPoint,
  StripSpec,
} from "@/types/market-intelligence";

export interface StripHistoryQuery {
  hub: string;
  /** Omit `tenors` to get all five. */
  spec: StripSpec | { mode: "rolling"; tenors?: readonly number[] };
  from: string;
  to?: string;
  /**
   * Every assessment date of a rolling series, rather than older dates thinned
   * to one a week. For statistics (the report's strip tables), not charts.
   */
  everyDay?: boolean;
}

export interface StripHistoryResponse {
  hub: string;
  commodity: MarketCommodity;
  unit: string;
  window: MarketWindow;
  /** Rolling mode yields one point per (date, tenor). */
  points: StripPoint[];
}

export interface OverviewCard {
  hub: string;
  /** ISO code for a power hub; a gas hub's region label ("Northeast") for gas. */
  iso: string;
  /**
   * 12-month rolling ATC. The sparkline tail only — NOT the window the stats
   * below describe.
   *
   * It used to be three years of assessments, and the card derived everything
   * from it. That does not survive real data: the mirror reaches 2012, so the
   * Overview's 54 hubs would ship ~169,000 rows to draw 54 sparklines. The
   * server now computes the stats over the full window and sends only the tail
   * it draws, so `series.length` says nothing about how much history backs
   * `percentile`.
   */
  series: StripPoint[];
  /**
   * Server-computed over the full window. Present on real data; absent from
   * the seeded generator, which leaves the card deriving from `series` as it
   * always did. Both paths must render — the module converts one surface per
   * release, so a card can be real while its neighbours are not.
   */
  stats?: OverviewCardStats;
  /**
   * What the card's numbers measure: the 12-month forward strip, or the daily
   * settled index of a gas region no forward curve quotes (AB#8626). Absent
   * from the seeded generator, whose cards are all strips.
   */
  measure?: OverviewMeasure;
}

/** `OverviewCardDto.measure`, narrowed from the wire's `string`. */
export type OverviewMeasure = "strip_12m" | "daily_index";

/** The numbers a card draws, computed server-side over the full window. */
export interface OverviewCardStats {
  hubName: string;
  /** Newest assessment backing `current`, ISO `yyyy-MM-dd`. */
  asOf: string;
  current: string | null;
  changeDay: string | null;
  /** 0-100, midpoint convention — matches `percentileRank` in `stats.ts`. */
  percentile: number | null;
}

export interface OverviewResponse {
  asOf: string;
  commodity: MarketCommodity;
  window: MarketWindow;
  cards: OverviewCard[];
  /**
   * EVERY point a region card can step through beyond its headline, as a
   * card: each point with a curve, and each point of a gas region no curve
   * quotes, on its daily index (AB#8626). The server sends these as their own
   * list. They used to be read off the movers, which rank strip moves only,
   * so an index point was never among them.
   */
  points: OverviewCard[];
  movers: MoverRow[];
  recs: RecCertificate[];
  /**
   * The capacity rows, or `null` when they could not be loaded.
   *
   * The distinction is load-bearing. `[]` means the auction genuinely
   * published nothing, which the panel states as a fact about the market;
   * `null` means the request failed, which is a fact about us. Folding the
   * second into the first made a capacity outage read on the module's
   * landing page as PJM having held no auction.
   */
  capacity: CapacityPoint[] | null;
}

export interface MoverRow {
  hub: string;
  hubName: string;
  iso: string;
  current: string | null;
  changeDay: string | null;
  changeWeek: string | null;
  changeMonth: string | null;
  changeYear: string | null;
}

/** One certificate's assessment history. */
export interface RecSeries {
  certificateId: string;
  name: string;
  iso: IsoCode;
  points: Array<{ assessDate: string; price: string | null }>;
}

export interface RegionHubSummary {
  hub: string;
  /** 12-month rolling ATC, one year of assessments. */
  strip12m: StripPoint[];
  latestIndex: IndexRow | null;
  /**
   * Display name and kind, from the server.
   *
   * Optional because the seeded generator sends neither and the models fall
   * back to the static topology for it. On real data they must NOT: the feed
   * publishes hubs that constant has never heard of, and falling back there is
   * what printed raw codes like `eastern_gas_north` on the gas cards.
   */
  name?: string;
  kind?: string;
  /**
   * Min/Max/Avg/Current per tenor over the trend window.
   *
   * The Pricing Trend grid shows these for every hub while its chart draws one.
   * Sending each hub's full daily series to fill that grid is what the mock
   * did; against the real feed a PJM region page would carry ~64,000 rows.
   */
  tenors?: TenorSummary[];
  /**
   * Whether a forward curve quotes this point.
   *
   * The region page groups on this. It is NOT `kind`: NYISO quotes all eleven
   * of its load zones while ISO-NE quotes three of its eight, so grouping by
   * hub-versus-zone would split NYISO for no reason and still leave ISO-NE
   * mixed. Tradeable against observable is the line that matters.
   *
   * Optional, defaulting to tradeable: the seeded generator sends no such
   * flag and every hub it publishes has a curve.
   */
  forwardQuoted?: boolean;
  /** This point's basis to its reference hub — what a settlement-only card leads with. */
  basis?: BasisSummary | null;
  /**
   * A settlement-only point's monthly settlements over the last year, oldest
   * first (AB#8626). Its card draws the last 12 as a sparkline, ranks the
   * newest among them, and shows its change from the month before.
   * Empty for a quoted point. Optional because the seeded generator sends none.
   */
  settledMonthly?: SettledMonth[];
}

/** One month's settlement: gas's mean daily assessment, or power's day-ahead ATC. */
export interface SettledMonth {
  /** The first of the month, ISO `yyyy-MM-dd`. */
  month: string;
  price: string;
}

/**
 * A settlement-only point's basis to its reference hub.
 *
 * What the grid cost on top of the hub price, for the newest month both
 * settled. Absent for a hub — a hub IS the reference — and whenever the two
 * sides have not settled the same month.
 */
export interface BasisSummary {
  parentHub: string;
  parentName: string;
  periodStart: string;
  daBasis: string;
}

/** One tenor's summary for one hub. */
export interface TenorSummary {
  numMonths: number;
  low: string | null;
  high: string | null;
  avg: string | null;
  current: string | null;
}

/** One region's points: an ISO's for power, a gas region's for gas. */
export interface RegionSummaryResponse {
  /** The ISO code, or the gas region's code ("Northeast"). */
  iso: string;
  asOf: string;
  window: MarketWindow;
  hubs: RegionHubSummary[];
}

export interface MarketIntelligenceSource {
  /** GET /api/market/topology */
  getTopology(): Promise<MarketTopology>;
  /** GET /api/market/overview?commodity&as_of */
  getOverview(q: { commodity: MarketCommodity; asOf?: string }): Promise<OverviewResponse>;
  /**
   * GET /api/market/regions/:iso/summary?commodity&as_of
   *
   * `iso` is an ISO code for power and a gas region's code for gas.
   */
  getRegionSummary(q: {
    iso: string;
    commodity: MarketCommodity;
    asOf?: string;
  }): Promise<RegionSummaryResponse>;
  /** GET /api/market/hubs/:hub/strips?mode&tenor&from&to */
  getStripHistory(q: StripHistoryQuery): Promise<StripHistoryResponse>;
  /**
   * GET /api/market/hubs/:hub/strip-periods
   *
   * Which calendar years and quarters this hub quotes. Empty from the seeded
   * generator, which serves no such endpoint.
   */
  getStripPeriods(q: { hub: string }): Promise<StripPeriod[]>;
  /** GET /api/market/hubs/:hub/forward-curve?as_of=a,b */
  getForwardCurve(q: { hub: string; asOfDates: string[] }): Promise<ForwardCurveVintage[]>;
  /** GET /api/market/hubs/:hub/index?granularity&from&to */
  getIndexSeries(q: { hub: string; months?: number }): Promise<IndexRow[]>;
  /** Daily settlements between two `yyyy-MM-dd` days, inclusive. */
  getIndexDaily(q: { hub: string; from: string; to: string }): Promise<IndexDailyRow[]>;
  /**
   * GET /api/market/hubs/:hub/index/hourly?days
   *
   * The intraday profile behind the monthly averages — `market.hourly_lmp`.
   * Bounded by `days` because an unbounded hourly series is 8,760 points a
   * year per hub and the chart cannot use that many.
   */
  getHourlyIndex(q: { hub: string; days?: number }): Promise<HourlyIndexRow[]>;
  /** GET /api/market/recs/series?iso&from */
  getRecSeries(q: { iso?: IsoCode; from?: string }): Promise<RecSeries[]>;
  /**
   * GET /api/market/regions/:iso/capacity
   *
   * `null` where the market clears no centralised auction, which the server
   * answers as a 404. Callers render the region's empty state rather than an
   * error: a market without a capacity auction is a fact about the market,
   * not a failure to load one.
   */
  getCapacity(q: { iso: IsoCode }): Promise<MarketCapacity | null>;
}

/** The query-key root, so every MI key is invalidated together. */
export const MI_QUERY_ROOT = "market-intelligence";

/**
 * The one place the SPA chooses a data source.
 *
 * This is the seam, and it now points at the real API. `httpMarketIntelligenceSource`
 * serves the converted surfaces and spreads the seeded generator underneath for
 * the rest, so the module converts one surface per release rather than flipping
 * whole. `MARKET_DATA_IS_REAL` in that file is the list of which is which, and
 * is what the UI reads to label a panel as sample data.
 */
let active: MarketIntelligenceSource = httpMarketIntelligenceSource;

export function getMarketIntelligenceSource(): MarketIntelligenceSource {
  return active;
}

/** Swap the implementation. Used by tests, and by the eventual API cutover. */
export function setMarketIntelligenceSource(source: MarketIntelligenceSource): void {
  active = source;
}
